import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Pencil, Eye, Trash2, Globe } from "lucide-react";
import { toast } from "sonner";
import { adminApi, fmtErr, fmtDate } from "../api";
import { useAuth } from "../AuthContext";
import { StatusChip, Btn, ConfirmDialog, Modal, inputCls, Field } from "../components";

const PagesList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pages, setPages] = useState(null);
  const [core, setCore] = useState([]);
  const [newTitle, setNewTitle] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [del, setDel] = useState(null);
  const [seo, setSeo] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    adminApi.get("/pages").then((r) => setPages(r.data.filter((p) => !p.id.startsWith("core--")))).catch((e) => toast.error(fmtErr(e)));
    adminApi.get("/core-pages").then((r) => setCore(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await adminApi.post("/pages", { title: newTitle });
      toast.success("Draft page created");
      navigate(`/admin/pages/${r.data.id}`);
    } catch (err) { toast.error(fmtErr(err)); setBusy(false); }
  };

  const togglePublish = async (p) => {
    try {
      await adminApi.post(`/pages/${p.id}/${p.status === "published" ? "unpublish" : "publish"}`);
      toast.success(p.status === "published" ? "Page unpublished" : "Page published");
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await adminApi.delete(`/pages/${del.id}`);
      toast.success("Page deleted");
      setDel(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };

  const saveSeo = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminApi.put(`/core-pages/${seo.key}`, { seo_title: seo.seo_title, seo_description: seo.seo_description });
      toast.success("SEO settings saved — live immediately");
      setSeo(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-pages-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Pages</h1>
          <p className="mt-1 text-sm text-slate-500">Create new website pages, and manage SEO titles/descriptions of the core pages.</p>
        </div>
        <Btn onClick={() => setNewOpen(true)} data-testid="add-page-btn"><Plus size={14} /> Add New Page</Btn>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-sm">
        <p className="px-6 py-4 border-b border-slate-200 font-display text-sm font-bold text-slate-900 flex items-center gap-2"><Globe size={15} className="text-[#C5221F]" /> Core Pages — SEO Settings</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="border-b border-slate-200 text-left">
                {["Page", "Path", "SEO Title", "Actions"].map((h) => (
                  <th key={h} className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {core.map((c) => (
                <tr key={c.key} className="border-b border-slate-100" data-testid={`core-page-${c.key}`}>
                  <td className="px-6 py-3 font-semibold text-slate-900 capitalize">{c.key}</td>
                  <td className="px-6 py-3 text-slate-500 font-mono-tech text-xs">{c.path}</td>
                  <td className="px-6 py-3 text-slate-600 text-xs max-w-[280px] truncate">{c.seo_title || <span className="text-slate-300">Default</span>}</td>
                  <td className="px-6 py-3">
                    <button onClick={() => setSeo({ ...c })} className="p-2 text-slate-500 hover:text-[#C5221F]" title="Edit SEO" data-testid={`edit-seo-${c.key}`}><Pencil size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <p className="px-6 py-4 border-b border-slate-200 font-display text-sm font-bold text-slate-900">Custom Pages</p>
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="border-b border-slate-200 text-left">
              {["Title", "URL", "Status", "Updated", "Actions"].map((h) => (
                <th key={h} className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pages === null ? (
              <tr><td colSpan={5} className="px-6 py-10 text-center text-slate-400">Loading…</td></tr>
            ) : pages.length === 0 ? (
              <tr><td colSpan={5} className="px-6 py-10 text-center text-slate-400">No custom pages yet — click "Add New Page".</td></tr>
            ) : (
              pages.map((p) => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`page-row-${p.id}`}>
                  <td className="px-6 py-3 font-semibold text-slate-900">{p.data?.title}</td>
                  <td className="px-6 py-3 text-slate-500 font-mono-tech text-xs">/p/{p.id}</td>
                  <td className="px-6 py-3"><StatusChip status={p.status} /></td>
                  <td className="px-6 py-3 text-xs text-slate-500">{fmtDate(p.updated_at)}</td>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-1.5">
                      <Link to={`/admin/pages/${p.id}`} className="p-2 text-slate-500 hover:text-[#C5221F]" data-testid={`edit-page-${p.id}`}><Pencil size={15} /></Link>
                      {p.status === "published" && <a href={`/p/${p.id}`} target="_blank" rel="noreferrer" className="p-2 text-slate-500 hover:text-[#C5221F]"><Eye size={15} /></a>}
                      <Btn variant="ghost" className="!px-2.5 !py-1.5" onClick={() => togglePublish(p)} data-testid={`toggle-page-${p.id}`}>{p.status === "published" ? "Unpublish" : "Publish"}</Btn>
                      {user.role === "super_admin" && (
                        <button onClick={() => setDel(p)} className="p-2 text-slate-400 hover:text-red-600" data-testid={`delete-page-${p.id}`}><Trash2 size={15} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal open={newOpen} onClose={() => setNewOpen(false)} title="Add New Page">
        <form onSubmit={create} className="space-y-4">
          <Field label="Page Title">
            <input className={inputCls} value={newTitle} onChange={(e) => setNewTitle(e.target.value)} required minLength={2} placeholder="e.g. Health & Safety" data-testid="new-page-title-input" />
          </Field>
          <div className="flex justify-end gap-3">
            <Btn variant="ghost" type="button" onClick={() => setNewOpen(false)}>Cancel</Btn>
            <Btn type="submit" disabled={busy} data-testid="create-page-submit-btn">Create Draft</Btn>
          </div>
        </form>
      </Modal>

      <Modal open={!!seo} onClose={() => setSeo(null)} title={`SEO — ${seo?.key || ""} page`}>
        {seo && (
          <form onSubmit={saveSeo} className="space-y-4">
            <Field label="SEO Title" hint="Leave blank to keep the default title.">
              <input className={inputCls} value={seo.seo_title} onChange={(e) => setSeo({ ...seo, seo_title: e.target.value })} maxLength={200} data-testid="core-seo-title-input" />
            </Field>
            <Field label="Meta Description">
              <textarea rows={3} className={inputCls} value={seo.seo_description} onChange={(e) => setSeo({ ...seo, seo_description: e.target.value })} maxLength={400} data-testid="core-seo-desc-input" />
            </Field>
            <div className="flex justify-end gap-3">
              <Btn variant="ghost" type="button" onClick={() => setSeo(null)}>Cancel</Btn>
              <Btn type="submit" disabled={busy} data-testid="core-seo-save-btn">Save</Btn>
            </div>
          </form>
        )}
      </Modal>

      <ConfirmDialog open={!!del} title="Delete this page?" message={`"${del?.data?.title}" will be permanently removed. This cannot be undone.`} onConfirm={doDelete} onClose={() => setDel(null)} busy={busy} />
    </div>
  );
};

export default PagesList;
